/** Форматирование значений для показа. */

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timezone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timezone}:${JSON.stringify(options)}`;
  let value = formatterCache.get(key);
  if (value === undefined) {
    value = new Intl.DateTimeFormat('ru-RU', { timeZone: timezone, ...options });
    formatterCache.set(key, value);
  }

  return value;
}

/** Дата и время в часовом поясе сообщества. */
export function formatDateTime(value: string | null, timezone: string, fallback = '—'): string {
  if (value === null) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return formatter(timezone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/** Короткая дата без года — для плотных таблиц. */
export function formatShortDate(value: string | null, timezone: string, fallback = '—'): string {
  if (value === null) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return formatter(timezone, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    date,
  );
}

/**
 * День без времени: «22 августа 2026».
 *
 * Отдельно от `formatShortDate`, в которой время есть: по ней разложили ленту
 * боёв по дням, и каждый бой оказался в своём «дне» — заголовки читались как
 * «22 авг., 19:14».
 */
export function formatDay(value: string | null, timezone: string, fallback = '—'): string {
  if (value === null) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return formatter(timezone, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/** Правильная форма слова после числа: 1 игрок, 2 игрока, 5 игроков. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100;
  const mod10 = mod100 % 10;

  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }

  if (mod10 === 1) {
    return one;
  }

  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }

  return many;
}

export function formatCount(count: number, one: string, few: string, many: string): string {
  return `${String(count)} ${plural(count, one, few, many)}`;
}

/** Сколько осталось до момента времени. */
export function formatRelative(value: string | null, now: Date = new Date()): string {
  if (value === null) {
    return 'время не указано';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'время не указано';
  }

  const diffMs = date.getTime() - now.getTime();
  const past = diffMs < 0;
  const totalMinutes = Math.max(1, Math.round(Math.abs(diffMs) / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${String(days)} д`);
  }
  if (hours > 0 && days < 3) {
    parts.push(`${String(hours)} ч`);
  }
  if (parts.length === 0) {
    parts.push(`${String(minutes)} мин`);
  }

  return past ? `${parts.join(' ')} назад` : `через ${parts.join(' ')}`;
}
